import { defineUtilityRegistry, type UtilityRouteDefinition } from '@saas-pages/shared/public/registry'

export type RouteKey = 'voice-recorder' | 'audio-trimmer' | 'circle-video-recorder'

export const voiceRecorderRoute = {
  routeKey: 'voice-recorder',
  path: '/diktofon-online',
  title: 'Диктофон онлайн',
  description: 'Запишите голос с микрофона, прослушайте и скачайте запись без отправки на сервер.',
  nav: { label: 'Диктофон', category: 'Аудио', order: 10 },
  seo: {
    title: 'Диктофон онлайн — записать голос бесплатно | mediatik.ru',
    description: 'Бесплатный диктофон в браузере: запись голоса с микрофона, пауза, прослушивание и скачивание. Без регистрации и загрузки на сервер.',
    changefreq: 'monthly', priority: 1,
  },
  processing: { mode: 'local', label: 'Локально в браузере', title: 'Аудиозапись остаётся на устройстве и не отправляется на сервер' },
  analyticsKey: 'voice-recorder', availability: 'available',
  schema: {
    applicationCategory: 'MultimediaApplication',
    howTo: ['Разрешите браузеру доступ к микрофону.', 'Начните запись; при необходимости поставьте её на паузу.', 'Остановите, прослушайте и скачайте готовый файл.'],
    faq: [
      { question: 'Запись отправляется на сервер?', answer: 'Нет. Запись создаётся локально средствами браузера и остаётся на вашем устройстве.' },
      { question: 'В каком формате сохранится голос?', answer: 'Формат зависит от браузера. Инструмент выбирает только реально поддерживаемый вариант и показывает его перед записью.' },
      { question: 'Почему браузер просит доступ к микрофону?', answer: 'Без разрешения сайт не может получить звуковой поток. Доступ используется только в открытой вкладке для текущей записи.' },
    ],
  },
  relatedTools: [{ routeKey: 'circle-video-recorder', label: 'Круглое видео', description: 'Запись с камеры и микрофона с круглым предпросмотром.' }],
} as const satisfies UtilityRouteDefinition<'voice-recorder', RouteKey>

export const audioTrimmerRoute = {
  routeKey: 'audio-trimmer',
  path: '/obrezat-audio',
  title: 'Обрезать аудио онлайн',
  description: 'Обрежьте один аудиофайл локально, сохранив исходный кодек и контейнер без перекодирования.',
  nav: { label: 'Обрезать аудио', category: 'Аудио', order: 15 },
  seo: {
    title: 'Обрезать аудио онлайн бесплатно без загрузки | mediatik.ru',
    description: 'Бесплатная локальная обрезка MP3, PCM WAV и аудио WebM/Opus. Предпросмотр и скачивание без загрузки файла на сервер.',
    changefreq: 'monthly', priority: 1,
  },
  processing: { mode: 'local', label: 'Локально в браузере', title: 'Локально в браузере — файл не отправляется на сервер' },
  analyticsKey: 'audio-trimmer', availability: 'available',
  schema: {
    applicationCategory: 'MultimediaApplication',
    howTo: ['Выберите один аудиофайл размером до 25 МБ.', 'Укажите начало и конец фрагмента и запустите обрезку.', 'Прослушайте результат и скачайте файл с исходным кодеком и контейнером.'],
    faq: [
      { question: 'Аудиофайл отправляется на сервер?', answer: 'Нет. Проверка, обрезка, предпросмотр и экспорт выполняются локально в браузере.' },
      { question: 'Изменится ли формат или качество звука?', answer: 'Нет. Инструмент копирует MP3-кадры, PCM-данные WAV или пакеты Opus в WebM и сохраняет исходные кодек и контейнер без перекодирования.' },
      { question: 'Почему граница может сдвинуться?', answer: 'Обрезка идёт по целым PCM-блокам, MP3-кадрам или пакетам WebM/Opus. Инструмент показывает фактический сохранённый диапазон.' },
    ],
  },
  relatedTools: [
    { routeKey: 'voice-recorder', label: 'Диктофон', description: 'Запишите новый аудиофайл с микрофона.' },
    { routeKey: 'circle-video-recorder', label: 'Круглое видео', description: 'Запишите короткое видео с камеры и микрофона.' },
  ],
} as const satisfies UtilityRouteDefinition<'audio-trimmer', RouteKey>
export const circleVideoRoute = {
  routeKey: 'circle-video-recorder',
  path: '/krugloe-video',
  title: 'Запись круглого видео',
  description: 'Запишите камеру в круглой рамке и скачайте квадратный видеофайл длительностью до 60 секунд.',
  nav: { label: 'Круглое видео', category: 'Видео', order: 20 },
  seo: {
    title: 'Записать круглое видео онлайн бесплатно | mediatik.ru',
    description: 'Локальная запись круглого видео с веб-камеры: круглая рамка, зеркальный режим, таймер до 60 секунд и скачивание квадратного файла.',
    changefreq: 'monthly', priority: 1,
  },
  processing: { mode: 'local', label: 'Локально в браузере', title: 'Камера и микрофон обрабатываются в браузере без загрузки видео на сервер' },
  analyticsKey: 'circle-video-recorder', availability: 'available',
  schema: {
    applicationCategory: 'MultimediaApplication',
    howTo: ['Разрешите доступ к камере и микрофону.', 'Настройте зеркальное отображение и начните запись.', 'Остановите запись или дождитесь лимита 60 секунд, затем скачайте квадратное видео.'],
    faq: [
      { question: 'Получится нативный видеокружок для мессенджера?', answer: 'Нет. Сайт создаёт обычный квадратный видеофайл. Конкретный мессенджер сам решает, как импортировать и показывать такой файл.' },
      { question: 'Видео загружается на сервер?', answer: 'Нет. Поток камеры, кадрирование, предпросмотр и готовый файл остаются в браузере.' },
      { question: 'Почему запись ограничена 60 секундами?', answer: 'Это безопасный предел по умолчанию для короткого видео и памяти вкладки. Запись автоматически остановится на отметке 60 секунд.' },
    ],
  },
  relatedTools: [{ routeKey: 'voice-recorder', label: 'Диктофон', description: 'Быстрая запись только с микрофона.' }],
} as const satisfies UtilityRouteDefinition<'circle-video-recorder', RouteKey>

export const mediatikUtilityRegistry = defineUtilityRegistry([voiceRecorderRoute, audioTrimmerRoute, circleVideoRoute])
